import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ConfirmModal from '../../../components/confirm-modal';
import useRemoveAssessRiskFromVehicle from '../../../data/vehicle/useRemoveAssessRiskFromVehicle';
import useGetVehicle, { type VehicleView } from '../../../data/vehicle-view/useGetVehicle';
import { useAuth } from '../../../State/Auth';
import { Button, ButtonContent } from '../../../styled-components/components/Button';
import Container from '../../../styled-components/components/Container';
import { H2 } from '../../../styled-components/components/Heading';
import { IconChevronLeft } from '../../../styled-components/components/Icon';
import Loader from '../../../styled-components/components/Loader';
import { Repeat, RepeatLarge } from '../../../styled-components/components/Repeat';
import { getMarketCodeFromToken } from '../../../utils/token';
import Code from './Code';

interface Vehicle {
  vehicleItemDoc?: {
    vehicle?: {
      vin?: string | null;
      registrationNumber?: string | null;
      isAssociatedWithRisk?: boolean | null;
    };
  }[];
}

interface DetailViewProps {
  type: keyof VehicleView;
}

const headings: Record<keyof VehicleView, string> = {
  importConfig: 'Import config',
  recordDetailDoc: 'Record detail document',
  searchServiceDoc: 'Search service document',
  vehicleItemDoc: 'Vehicle item document',
};

const DetailView = ({ type }: DetailViewProps) => {
  const { decodedToken } = useAuth();
  const marketCode = getMarketCodeFromToken(decodedToken);
  const params = useParams();
  const navigate = useNavigate();
  const [removeAssessRisk, requestRemoveAssessRisk] = useRemoveAssessRiskFromVehicle();
  const [pendingRemoveRiskAssessment, setPendingRemoveRiskAssessment] = useState(false);
  const [collapsedCode, setCollapsedCode] = useState(true);

  if (!params.vehicle) {
    return null;
  }

  const goBack = () => {
    navigate(`/vehicle-view?search=${params.vehicle}`);
  };

  const [vehicle, requestVehicle] = useGetVehicle(params.vehicle);

  const vin = (vehicle as unknown as Vehicle)?.vehicleItemDoc?.[0]?.vehicle?.vin;
  const isAssociatedWithRisk = !!(vehicle as unknown as Vehicle)?.vehicleItemDoc?.[0]?.vehicle
    ?.isAssociatedWithRisk;

  const onRemoveAssessRisk = useCallback(() => {
    if (vin) {
      removeAssessRisk({ vin });
    }
  }, [vin]);

  useEffect(() => {
    if (requestRemoveAssessRisk.isSuccess) {
      setPendingRemoveRiskAssessment(false);
    }
  }, [requestRemoveAssessRisk.isSuccess]);

  return (
    <>
      {pendingRemoveRiskAssessment && (
        <ConfirmModal
          heading="Remove risk assessment from vehicle?"
          body={`Are you sure you want to remove the risk assessment from vehicle "${vin}"?`}
          confirmButtonTone="danger"
          confirmButtonText="Remove risk assessment"
          confirmButtonLoading={requestRemoveAssessRisk.isLoading}
          onCancel={() => setPendingRemoveRiskAssessment(false)}
          onConfirm={() => onRemoveAssessRisk()}
        />
      )}
      <Container>
        <RepeatLarge>
          <h1>{params.vehicle}</h1>
        </RepeatLarge>
        <RepeatLarge>
          <Button $variant="ghost" $tone="primary" onClick={goBack}>
            <ButtonContent>
              <IconChevronLeft />
            </ButtonContent>
            <ButtonContent>Go back</ButtonContent>
          </Button>
        </RepeatLarge>
        {marketCode === 'SE' && (
          <RepeatLarge>
            <Button
              $variant="ghost"
              $tone="primary"
              onClick={() => setPendingRemoveRiskAssessment(true)}
              disabled={!vin || !isAssociatedWithRisk || requestRemoveAssessRisk.isLoading}
            >
              <ButtonContent>Remove risk assessment</ButtonContent>
              {requestRemoveAssessRisk.isLoading && (
                <ButtonContent>
                  <Loader />
                </ButtonContent>
              )}
            </Button>
          </RepeatLarge>
        )}
        <RepeatLarge>
          <Button $variant="ghost" $tone="primary" onClick={() => setCollapsedCode(!collapsedCode)}>
            <ButtonContent>{collapsedCode ? 'Show all' : 'Show less'}</ButtonContent>
          </Button>
        </RepeatLarge>
        <RepeatLarge>
          {requestVehicle.isLoading && <Loader />}
          {requestVehicle.isError && <div>Failed to load vehicle</div>}
          <Repeat>
            <H2 $noMargin>{headings[type]}</H2>
          </Repeat>
          <Repeat>
            <Code code={(vehicle && vehicle[type]) ?? ''} collapsed={collapsedCode} />
          </Repeat>
        </RepeatLarge>
      </Container>
    </>
  );
};

export default DetailView;
